import { Copy, Trash2 } from 'lucide-react'
import { useEffect } from 'react'

import { Boton, Tarjeta, clases } from '../componentes/ui'
import { CampoGenerico } from '../motor/Campo'
import { type Errores, hayErroresEnAlguno } from '../motor/errores'
import type { Campo } from '../motor/esquema'
import { unir } from '../motor/rutas'
import type { Seccion } from './ajustes'
import { DondeSale } from './DondeSale'

/**
 * La pestaña «Ajustes» del constructor: un menú con las secciones (que salen de cómo agrupa
 * el núcleo los campos del formulario, `ajustes.ts`) y, al lado, la sección elegida con los
 * controles del motor. La sección con un error lleva su punto rojo, y al guardar con errores
 * se abre la primera que los tenga.
 */
const rutasDe = (campos: Campo[]): string[] =>
  campos.flatMap((c) => (c.nombre ? [c.nombre] : rutasDe(c.campos ?? [])))

/** Una sección de un solo grupo o de una sola lista se pinta sin repetir su nombre: ya lo dice el título. */
const CamposDeLaSeccion = ({ seccion }: { seccion: Seccion }) => {
  const [unico] = seccion.campos
  if (seccion.campos.length === 1 && unico?.nombre && unico.tipo === 'group') {
    return (
      <CampoGenerico
        campo={{ ...unico, sinEtiqueta: true, descripcion: undefined }}
        ruta={unico.nombre}
      />
    )
  }
  if (seccion.campos.length === 1 && unico?.nombre && unico.tipo === 'array') {
    return (
      <CampoGenerico
        campo={{
          ...unico,
          etiqueta: unico.etiquetas?.plural ?? unico.etiqueta,
          descripcion: undefined,
        }}
        ruta={unico.nombre}
      />
    )
  }
  return (
    <div className="flex flex-col gap-5">
      {seccion.campos.map((c, i) => (
        <CampoGenerico campo={c} key={c.nombre ?? i} ruta={unir('', c.nombre)} />
      ))}
    </div>
  )
}

export const AjustesDelFormulario = ({
  secciones,
  activa,
  alCambiar,
  errores,
  espacio,
  sitio,
  id,
  puedeBorrar,
  puedeDuplicar,
  sucio,
  alBorrar,
  alDuplicar,
}: {
  activa: string
  alBorrar: () => void
  alCambiar: (clave: string) => void
  alDuplicar: () => void
  errores: Errores
  espacio: number
  id: null | number
  puedeBorrar: boolean
  puedeDuplicar: boolean
  secciones: Seccion[]
  sitio: number
  sucio: boolean
}) => {
  const conErrores = new Set(
    secciones.filter((s) => hayErroresEnAlguno(errores, rutasDe(s.campos))).map((s) => s.clave),
  )
  const seccion = secciones.find((s) => s.clave === activa) ?? secciones[0]

  // Si la elegida deja de existir (otro núcleo), la primera.
  useEffect(() => {
    if (secciones.length > 0 && !secciones.some((s) => s.clave === activa))
      alCambiar(secciones[0]!.clave)
  }, [secciones, activa, alCambiar])

  return (
    <div className="grid gap-6 lg:grid-cols-[15rem_minmax(0,52rem)]">
      <nav
        aria-label="Secciones de los ajustes"
        className="flex gap-1 overflow-x-auto lg:sticky lg:top-44 lg:flex-col lg:self-start lg:overflow-visible"
      >
        {secciones.map((s) => (
          <button
            aria-current={seccion?.clave === s.clave ? 'page' : undefined}
            className={clases(
              'flex shrink-0 items-center justify-between gap-2 rounded-boton px-3 py-2 text-left text-sm whitespace-nowrap transition-colors lg:whitespace-normal',
              seccion?.clave === s.clave
                ? 'bg-papel font-medium text-tinta shadow-suave ring-1 ring-linea'
                : 'text-tinta-2 hover:bg-papel-3 hover:text-tinta',
            )}
            key={s.clave}
            onClick={() => alCambiar(s.clave)}
            type="button"
          >
            {s.etiqueta}
            {conErrores.has(s.clave) && (
              <span aria-label="con errores" className="size-1.5 shrink-0 rounded-full bg-error" />
            )}
          </button>
        ))}
      </nav>

      {seccion && (
        <div className="flex min-w-0 flex-col gap-5">
          <Tarjeta className="flex flex-col gap-5">
            <div>
              <h2 className="font-display text-lg font-semibold">{seccion.etiqueta}</h2>
              {seccion.descripcion && (
                <p className="mt-1 text-sm text-tenue">{seccion.descripcion}</p>
              )}
            </div>
            <CamposDeLaSeccion seccion={seccion} />
          </Tarjeta>

          {seccion.clave === secciones[0]?.clave && id !== null && (
            <>
              <DondeSale espacio={espacio} formulario={id} sitio={sitio} />
              {(puedeDuplicar || puedeBorrar) && (
                <Tarjeta className="flex flex-col gap-3">
                  <p className="text-xs font-medium tracking-wide text-tenue uppercase">
                    Este formulario
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {puedeDuplicar && (
                      <Boton
                        disabled={sucio}
                        icono={<Copy />}
                        onClick={alDuplicar}
                        pequeno
                        title={
                          sucio
                            ? 'Guarda antes de duplicar: la copia se hace de lo guardado.'
                            : undefined
                        }
                      >
                        Duplicar
                      </Boton>
                    )}
                    {puedeBorrar && (
                      <Boton icono={<Trash2 />} onClick={alBorrar} pequeno tipo="peligro">
                        Borrar
                      </Boton>
                    )}
                  </div>
                  <p className="text-xs text-tenue">
                    Un formulario con respuestas guardadas no se borra: son datos de personas.
                    Ciérralo en vez de borrarlo, o exporta y borra antes sus respuestas.
                  </p>
                </Tarjeta>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
